import React from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";
import { LOGO_URL, COMPANY } from "@/lib/company";

export const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = React.useState(false);

  const links = [
    { to: "/", label: t.nav.home, end: true },
    { to: "/leistungen", label: t.nav.services },
    { to: "/ueber-uns", label: t.nav.about },
    { to: "/team", label: t.nav.team },
    { to: "/kontakt", label: t.nav.contact },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors duration-200 ${
      isActive ? "text-[#E8620E]" : "text-[#0A0A0A] hover:text-[#E8620E]"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-[#E5E5E5] bg-white/90 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="header-logo-link" className="flex items-center">
          <img
            src={LOGO_URL}
            alt="Elvocom AG"
            className="h-11 w-auto object-contain md:h-12"
          />
        </Link>

        <nav className="hidden items-center gap-9 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={linkClass}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.replace("/", "")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <div className="flex items-center gap-1" data-testid="language-switcher">
            {LANGUAGES.map((l, i) => (
              <React.Fragment key={l.code}>
                {i > 0 && <span className="text-[#E5E5E5]">/</span>}
                <button
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-${l.code}`}
                  className={`text-sm font-semibold transition-colors ${
                    lang === l.code
                      ? "text-[#E8620E]"
                      : "text-[#757575] hover:text-[#0A0A0A]"
                  }`}
                >
                  {l.label}
                </button>
              </React.Fragment>
            ))}
          </div>
          <Link
            to="/kontakt"
            data-testid="header-cta-button"
            className="bg-[#0A0A0A] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#E8620E]"
          >
            {t.nav.cta}
          </Link>
        </div>

        <button
          className="lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[#E5E5E5] bg-white lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 text-base font-medium ${
                    isActive ? "text-[#E8620E]" : "text-[#0A0A0A]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-3 flex items-center gap-3 border-t border-[#E5E5E5] pt-4">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`text-sm font-semibold ${
                    lang === l.code ? "text-[#E8620E]" : "text-[#757575]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
              <a
                href={COMPANY.phoneHref}
                className="ml-auto flex items-center gap-2 text-sm font-semibold text-[#0A0A0A]"
              >
                <Phone size={16} /> {COMPANY.phone}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
